import React, { useEffect } from 'react';

import { Grid, Stack, Title } from '@mantine/core';
import { AIConnectCard } from 'components/AIConnect';
import PageHeader from 'components/PageHeader';
import { useAuth } from 'context/auth-context';
import useGym from 'hooks/gym/useGym';
import { useSearchParams } from 'react-router-dom';
import notificationHandler from 'utils/notifications';

import AdditionalSettingsCard from './components/AdditionalSettingsCard';
import AthleteAppSettingsCard from './components/AthleteAppSettingsCard';
import GeneralCard from './components/GeneralCard';
import NotificationsCard from './components/NotificationsCard';
import ReservationWaitlistCancelationsCard from './components/ReservationWaitlistCancelationsCard';
import StripeTerminalReaderCard from './components/StripeTerminalReaderCard';

const GymSettings = (): JSX.Element => {
  const { session, hasPerm } = useAuth();
  const { data, isLoading, isSuccess } = useGym(session?.gym?.gymId);
  const [searchParams, setSearchParams] = useSearchParams();
  const { errorNotification } = notificationHandler();

  useEffect(() => {
    if (searchParams.get('glm_error') === 'already_linked') {
      errorNotification(
        'Gym Lead Machine connection failed',
        'This Gym Lead Machine subaccount is already connected to another gym. Each subaccount can only be linked to one gym.',
      );
      searchParams.delete('glm_error');
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get('glm_error') === 'invalid_state') {
      errorNotification(
        'Gym Lead Machine connection failed',
        'The connection link expired or was already used. Click Connect again to retry.',
      );
      searchParams.delete('glm_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [errorNotification, searchParams, setSearchParams]);
  const isImpersonating = session?.isImpersonating || false;

  const settingsPerm = hasPerm('GymMutate');
  // Owner-only: an MCP token reads the whole gym's revenue and membership, so the surface
  // is not opened up to staff even where MCPConnectionMutate has been granted.
  const showAIConnect = session?.role === 'owner';

  return (
    <>
      <PageHeader>
        <Title order={1}>Gym Settings</Title>
      </PageHeader>
      <Stack p='xl'>
        <Grid>
          <Grid.Col xs={12} md={4}>
            <Stack>
              <GeneralCard
                permission={settingsPerm}
                gymData={data?.data}
                gymDataIsLoading={isLoading}
                gymDataIsSuccess={isSuccess}
                isImpersonating={isImpersonating}
              />
              <StripeTerminalReaderCard gymData={data?.data} gymDataIsLoading={isLoading} />
            </Stack>
          </Grid.Col>
          <Grid.Col xs={12} md={8}>
            <ReservationWaitlistCancelationsCard
              permission={settingsPerm}
              gymData={data?.data}
              gymDataIsLoading={isLoading}
              gymDataIsSuccess={isSuccess}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <AthleteAppSettingsCard
              permission={settingsPerm}
              gymData={data?.data}
              gymDataIsLoading={isLoading}
              gymDataIsSuccess={isSuccess}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            {isSuccess && (
              <NotificationsCard
                permission={settingsPerm}
                gymData={data?.data}
                gymDataIsLoading={isLoading}
                gymDataIsSuccess={isSuccess}
              />
            )}
          </Grid.Col>
          <Grid.Col span={12}>
            <AdditionalSettingsCard
              permission={settingsPerm}
              gymData={data?.data}
              gymDataIsLoading={isLoading}
              gymDataIsSuccess={isSuccess}
            />
          </Grid.Col>
          {showAIConnect && (
            <Grid.Col span={12}>
              <AIConnectCard gymTitle={session?.gym?.title} />
            </Grid.Col>
          )}
        </Grid>
      </Stack>
    </>
  );
};

export default GymSettings;
